import { FormikProps } from "formik";
import * as Yup from "yup";
import {
  PatientVitalFlagEnum,
  PatientVitalRequest,
} from "../../../../../../../services";

export enum VitalsEnumIndex {
  "bloodPressure",
  "bloodGlucose",
  "bodyTemperature",
  "heartRate",
  "respirationRate",
  "height",
  "weight",
  "o2_saturation",
  "pulseRate",
  "bmi",
  "respiratoryVolume",
  "perfusionIndex",
  "peakExpiratoryFlow",
  "forceExpiratoryVolume",
}

export type Vitals =
  | "bloodPressure"
  | "bloodGlucose"
  | "bodyTemperature"
  | "heartRate"
  | "respirationRate"
  | "height"
  | "weight"
  | "o2_saturation"
  | "pulseRate"
  | "bmi"
  | "respiratoryVolume"
  | "perfusionIndex"
  | "peakExpiratoryFlow"
  | "forceExpiratoryVolume";

export interface PatientVitalCustom {
  patient?: { [key: string]: string };
  name: string;
  systolic?: string;
  diastolic?: string;
  pulse?: string;
  flag?: PatientVitalFlagEnum;
  value?: string;
  unit?: string;
  method?: string;
  type?: string;
  // 'recordedDate'?: string;
  // 'note'?: string;
}

export enum TakenAtLocation {
  IN_OFFICE = "IN_OFFICE",
  AT_HOME = "AT_HOME",
}

interface VitalOtherFields {
  recordedDate?: Date;
  uuid?: string;
  takenAtLocation?: TakenAtLocation;
}

export interface CustomVitalData
  extends Omit<
      PatientVitalRequest,
      "patient" | "recordedDate" | "uuid" | "takenAtLocation"
    >,
    VitalOtherFields {}

export type VitalFormik = FormikProps<CustomVitalData>;

// extends PatientVitalRequest
// 'patient': { [key: string]: string; };
// 'note'?: string;
// 'recordedDate'?: string;
// 'patientVitals'?: Array<PatientVital>;

export const INITIAL_VITALS = [
  {
    selected: false,
    name: "bloodPressure",
    label: "Blood Pressure",
    unit: "mmHg",
    value: undefined,
    systolic: undefined,
    diastolic: undefined,
  },
  {
    selected: false,
    name: "bloodGlucose",
    label: "Blood Glucose",
    unit: "mg/dL",
    value: undefined,
  },
  {
    selected: false,
    name: "bodyTemperature",
    label: "Body Temperature",
    unit: "f",
    value: undefined,
  },
  {
    selected: false,
    name: "heartRate",
    label: "Heart Rate",
    unit: "BPM",
    value: undefined,
  },
  {
    selected: false,
    name: "respirationRate",
    label: "Respiration Rate",
    unit: "BPM",
    value: undefined,
  },
  {
    selected: false,
    name: "height",
    label: "Height",
    unit: "CM",
    value: undefined,
  },
  {
    selected: false,
    name: "weight",
    label: "Weight",
    unit: "lbs",
    value: undefined,
  },
  {
    selected: false,
    name: "o2_saturation",
    label: "Oxygen Saturation (SpO2)",
    unit: "%",
    value: undefined,
  },
  {
    selected: false,
    name: "pulseRate",
    label: "Pulse Rate",
    unit: "BPM",
    value: undefined,
  },
  {
    selected: false,
    name: "bmi",
    label: "Body Mass Index",
    unit: "kg/m^2",
    value: undefined,
  },
  {
    selected: false,
    name: "respiratoryVolume",
    label: "Respiratory Volume",
    unit: "ml",
    value: undefined,
  },
  {
    selected: false,
    name: "perfusionIndex",
    label: "Perfusion Index",
    unit: "%",
    value: undefined,
  },
  {
    selected: false,
    name: "peakExpiratoryFlow",
    label: "Peak Expiratory Flow",
    unit: "l/min",
    value: undefined,
  },
  {
    selected: false,
    name: "forceExpiratoryVolume",
    label: "Forced Expiratory Volume",
    unit: "l",
    value: undefined,
  },
];

export const VITAL_INITIAL_VALUE = {
  patientVitals: INITIAL_VITALS,
  recordedDate: new Date(),
};

export const UnitMapping: Record<Vitals, string> = {
  bloodPressure: "mmHg",
  bloodGlucose: "mg/dL",
  bodyTemperature: "°F",
  heartRate: "BPM",
  respirationRate: "BPM",
  height: "m",
  weight: "lbs",
  o2_saturation: "%",
  pulseRate: "BPM",
  bmi: "kg/m^2",
  respiratoryVolume: "ml",
  perfusionIndex: "%",
  peakExpiratoryFlow: "l/min",
  forceExpiratoryVolume: "l",
};

export const vitalTitleMapper: Record<Vitals, string> = {
  bloodPressure: "Blood Pressure",
  bloodGlucose: "Blood Glucose",
  bodyTemperature: "Body Temperature",
  heartRate: "Heart Rate",
  respirationRate: "RespirationRate",
  height: "Height",
  weight: "Weight",
  o2_saturation: "O2 Saturation",
  pulseRate: "Pulse Rate",
  bmi: "Bmi",
  respiratoryVolume: "Respiratory Volume",
  perfusionIndex: "Perfusion Index",
  peakExpiratoryFlow: "Peak Expiratory Flow",
  forceExpiratoryVolume: "Force Expiratory Volume",
};

export const optionsHere = [
  {
    value: "bloodPressure",
    label: "Blood Pressure",
    selected: true,
  },
  {
    value: "bloodGlucose",
    label: "Blood Glucose",
    selected: true,
  },
  {
    value: "bodyTemperature",
    label: "Body Temperature",
    selected: true,
  },
  { value: "heartRate", label: "Heart Rate", selected: true },
  {
    value: "respirationRate",
    label: "Respiration Rate",
    selected: true,
  },
  { value: "height", label: "Height", selected: true },
  { value: "weight", label: "Weight", selected: true },
  {
    value: "o2_saturation",
    label: "Oxygen Saturation (SpO2)",
    selected: true,
  },
  { value: "pulseRate", label: "Pulse Rate", selected: true },
  { value: "bmi", label: "Body Mass Index", selected: true },
  {
    value: "respiratoryVolume",
    label: "Respiratory Volume",
    selected: true,
  },
  {
    value: "perfusionIndex",
    label: "Perfusion Index",
    selected: true,
  },
  {
    value: "peakExpiratoryFlow",
    label: "Peak Expiratory Flow",
    selected: true,
  },
  {
    value: "forceExpiratoryVolume",
    label: "Forced Expiratory Volume",
    selected: true,
  },
];

// ----------------------------------------------------------------------------------------------
// all child constant fields

export const BLOOD_PRESSURE_SITTING_OPTS = [
  { value: "sitting", label: "Sitting" },
  { value: "standing", label: "Standing" },
];

export const BLOOD_PRESSURE_ARM_OPTS = [
  { value: "lArm", label: "L Arm" },
  { value: "rArm", label: "R Arm" },
];

export const BLOOD_PRESSURE_ADULT_OPTS = [
  { value: "adult", label: "Adult" },
  { value: "smallAdult", label: "Small Adult" },
  { value: "largeAdult", label: "Large Adult" },
];

export const FLAG_OPTS = [
  { value: "NORMAL", label: "Normal" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HIGH", label: "High" },
];

export const MEAL_OPTS = [
  { value: "beforeMeal", label: "Before Meal" },
  { value: "afterMeal", label: "After Meal" },
  { value: "afterWakingUp", label: "After Waking Up" },
];

export const LUNCH_TYPE_OPTS = [
  {
    label: "Breakfast",
    value: "breakfast",
  },
  {
    label: "Lunch",
    value: "lunch",
  },
  {
    label: "Evening Snack",
    value: "evening_snack",
  },
  {
    label: "Dinner",
    value: "dinner",
  },
];

export const TEMPERATURE_OPTS = [
  { value: "f", label: "F" },
  { value: "c", label: "C" },
];

export const ORAL_OPTS = [
  { value: "oral", label: "Oral" },
  { value: "rectal", label: "Rectal" },
  { value: "armpit", label: "Armpit (Axillary)" },
  { value: "ear", label: "Ear" },
  { value: "skin", label: "Skin" },
  { value: "internally", label: "Internally" },
];

export const HEIGHT_UNIT_OPTS = [
  { value: "m", label: "M" },
  { value: "cm", label: "CM" },
  { value: "ft", label: "Ft" },
  { value: "inch", label: "Inch" },
  // { value: "ft_inch", label: "Ft-Inch" },
];

export const HEIGHT_SITTING_OPTS = [
  { value: "standing", label: "Standing" },
  { value: "sitting", label: "Sitting" },
];

export const WEIGHT_UNIT_OPTS = [
  { value: "g", label: "G" },
  { value: "kg", label: "Kg" },
  { value: "lbs", label: "Lbs" },
  { value: "oz", label: "Oz" },
];

export const WEIGHT_METHOD_OPTS = [
  { value: "dry", label: "Dry" },
  { value: "wet", label: "Wet" },
  { value: "clothesOn", label: "Clothes On" },
];

export const WEIGHT_TYPE_OPTS = [
  { value: "postDialysis", label: "Post Dialysis" },
  { value: "preDialysis", label: "Pre Dialysis" },
  { value: "postFasting", label: "Post Fasting" },
  { value: "preFasting", label: "Pre Fasting" },
];

export const OXYGEN_OPTS = [
  { value: "oxygen", label: "Oxygen" },
  { value: "breathingTreatment", label: "Breathing Treatment" },
  {
    value: "roomAirWithOxygen",
    label: "Room Air With Oxygen",
  },
  {
    value: "pulseOximeter",
    label: "Pulse Oximeter",
  },
];

export const PULSE_RATE_OPTS = [
  { value: "regular", label: "Regular" },
  { value: "irregular", label: "Irregular" },
  { value: "regularly irregular", label: "Regularly Irregular" },
  {
    value: "irregularly irregular",
    label: "Irregularly Irregular",
  },
];

export const RESPIRATORY_OPTS = [
  { value: "ml", label: "mL" },
  { value: "l", label: "L" },
];

export const TAKEN_AT_LOCATION_OPTS = [
  { value: "CLINIC", label: "Clinic" },
  { value: "HOME", label: "Home" },
];

export const getVitalValidationSchema = (selectedVitalNames: string[]) =>
  Yup.object().shape({
    patientVitals: Yup.array().of(
      Yup.object().shape({
        selected: Yup.boolean(),
        name: Yup.string(),
        value: Yup.string().test(
          "required-if-selected",
          "This field is required",
          function (value) {
            const { selected, name } = this.parent;
            if (!selected) return true;
            if (selectedVitalNames.includes(name)) {
              return !!value;
            }
            return true;
          }
        ),
        systolic: Yup.string().test(
          "required-if-selected",
          "This field is required",
          function (value) {
            const { selected, name } = this.parent;
            if (!selected || name !== "bloodPressure") return true;
            if (selectedVitalNames.includes("bloodPressure")) {
              return !!value;
            }
            return true;
          }
        ),
        diastolic: Yup.string().test(
          "required-if-selected",
          "This field is required",
          function (value) {
            const { selected, name } = this.parent;
            if (!selected || name !== "bloodPressure") return true;
            if (selectedVitalNames.includes("bloodPressure")) {
              return !!value;
            }
            return true;
          }
        ),
      })
    ),
    recordedDate: Yup.string().required("Recorded date is required"),
    takenAtLocation: Yup.string(),
  });
